import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { ArrowLeft, ArrowRight, Sparkles, Rocket, Loader2, ChevronRight } from 'lucide-react';
import { Section } from '@/components/shared/Section';

// ============================================================================
// /novedades — Public-facing changelog of major ALLCOM platform releases.
// Sourced from /api/releases — admins can publish new entries from /admin/releases.
// SEO long-tail: "what's new at allcom" type queries.
// ============================================================================

const TAG_COLORS = {
  Feature: 'bg-sky-50 text-[#0EA5E9] border-sky-200',
  Aeropuertos: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  Admin: 'bg-amber-50 text-amber-700 border-amber-200',
  SEO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Seguridad: 'bg-red-50 text-red-600 border-red-200',
  UX: 'bg-violet-50 text-violet-700 border-violet-200',
  Editorial: 'bg-pink-50 text-pink-700 border-pink-200',
  IA: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  AVX: 'bg-slate-100 text-slate-800 border-slate-300',
  Performance: 'bg-orange-50 text-orange-700 border-orange-200',
  Calculator: 'bg-teal-50 text-teal-700 border-teal-200',
  Catálogo: 'bg-blue-50 text-blue-700 border-blue-200',
  Reliability: 'bg-lime-50 text-lime-700 border-lime-200',
  Fix: 'bg-slate-50 text-slate-600 border-slate-200',
};
const FALLBACK_TAG = 'bg-slate-50 text-slate-600 border-slate-200';

const renderIcon = (name, className) => {
  const Icon = (name && LucideIcons[name]) || Sparkles;
  return <Icon className={className} strokeWidth={1.6} />;
};

export default function NovedadesPage() {
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = 'Novedades y releases · ALLCOM';
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/releases`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        // NovedadesPage shows only published releases; drafts are admin-only
        setReleases(list.filter((r) => r.published !== false));
      })
      .catch(() => { setError(true); setReleases([]); });
  }, []);

  const isLoading = releases === null;

  return (
    <div className="bg-white" data-testid="novedades-page">
      {/* Hero */}
      <div className="relative bg-[#050E1F] text-white overflow-hidden">
        <div className="absolute inset-0 noise-texture opacity-30" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0EA5E9]/10 blur-3xl rounded-full" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F8D90F]/8 blur-3xl rounded-full" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:text-white mb-6">
            <ArrowLeft className="w-3 h-3" /> Volver al sitio
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0EA5E9] bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-full">
            <Sparkles className="w-3 h-3" /> Novedades · Releases
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-4" style={{ fontFamily: 'Syne,sans-serif' }}>
            La plataforma <span className="text-[#0EA5E9]">evoluciona</span><br className="hidden sm:block" />
            cada semana.
          </h1>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Aquí publicamos los cambios más relevantes del sitio: nuevos productos, módulos B2B, herramientas de admin, mejoras de SEO técnico y refinamiento de UX. Sin agencias intermedias, hecho con velocidad de startup y rigor B2B.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contacto" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg" data-testid="novedades-cta-cotizar">
              Solicitar cotización <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/empresa/noticias" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-white/10 backdrop-blur border border-white/20 rounded-xl hover:bg-white/15">
              Kian Daily News <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <Section>
        <div className="max-w-3xl mx-auto">
          {isLoading ? (
            <div className="py-16 text-center text-slate-400" data-testid="novedades-loading">
              <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" />
              <p className="text-sm">Cargando releases…</p>
            </div>
          ) : releases.length === 0 ? (
            <div className="py-16 text-center text-slate-400 max-w-md mx-auto" data-testid="novedades-empty">
              <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40" strokeWidth={1.4} />
              <p className="text-sm">{error ? 'No pudimos cargar las novedades. Reintenta en unos segundos.' : 'Pronto publicaremos las primeras releases.'}</p>
            </div>
          ) : releases.map((release, idx) => (
            <motion.article
              key={release.id || release.version}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              className="relative pl-8 sm:pl-12 pb-12 last:pb-0 border-l-2 border-slate-200 last:border-l-transparent"
              data-testid={`release-${release.version}`}
            >
              <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 flex items-center justify-center shadow-sm" style={{ borderColor: release.color || '#0EA5E9' }}>
                <div className="w-2 h-2 rounded-full" style={{ background: release.color || '#0EA5E9' }} />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow p-6 sm:p-8">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: release.color || '#0EA5E9' }}>
                    {renderIcon(release.icon, 'w-5 h-5')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-xl font-bold text-[#0A2540] tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }}>{release.version}</span>
                      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{release.date}</span>
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-[#0A2540] leading-tight" style={{ fontFamily: 'Syne,sans-serif' }}>{release.headline}</h2>
                    <p className="text-sm text-[#64748B] leading-relaxed mt-2">{release.intro}</p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2.5">
                  {(release.items || []).map((item, i) => {
                    const tagCls = TAG_COLORS[item.tag] || FALLBACK_TAG;
                    return (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border ${tagCls}`}>{item.tag}</span>
                        <span className="text-[#475569] leading-relaxed flex-1 min-w-0">{item.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.article>
          ))}

          {!isLoading && releases.length > 0 && (
            <div className="mt-8 p-8 sm:p-10 rounded-2xl bg-gradient-to-br from-slate-50 to-sky-50/40 border border-slate-200 text-center">
              <Rocket className="w-10 h-10 mx-auto mb-3 text-[#0EA5E9]" strokeWidth={1.4} />
              <h3 className="text-xl sm:text-2xl font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Syne,sans-serif' }}>
                ¿Tu equipo necesita algo que no ves listado?
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed max-w-xl mx-auto mb-5">
                Construimos features bajo demanda. Si tu operación necesita un módulo específico (integración ERP, analytics custom, IA en tu vertical), lo evaluamos y entregamos en sprints semanales.
              </p>
              <Link to="/contacto" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#0A2540] rounded-xl hover:bg-slate-800 shadow-lg" data-testid="novedades-cta-feature-request">
                Proponer una feature <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
